import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;
export const API = `${BASE}/api`;

const client = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// --- Silent session keep-alive -------------------------------------------
// The access-token cookie is short-lived (24h) but the refresh cookie lasts a
// week. When a request 401s because the access token lapsed, transparently hit
// /auth/refresh once (single-flight) and replay the original request, so an
// active session survives access-token expiry instead of bouncing to /login.
// Skipped for the auth-transition endpoints themselves to avoid loops.
const NO_REFRESH = ["/auth/refresh", "/auth/login", "/auth/logout", "/auth/register"];
let refreshing = null;

function tryRefresh() {
  if (!refreshing) {
    refreshing = client.post("/auth/refresh").finally(() => {
      refreshing = null;
    });
  }
  return refreshing;
}

client.interceptors.response.use(
  (res) => res,
  async (error) => {
    const { response, config } = error;
    if (!response || response.status !== 401 || !config || config._retry) {
      return Promise.reject(error);
    }
    const url = config.url || "";
    if (NO_REFRESH.some((p) => url.includes(p))) return Promise.reject(error);
    config._retry = true;
    try {
      await tryRefresh();
      return client(config); // replay with the freshly-issued access cookie
    } catch (refreshErr) {
      // Refresh unreachable (network / 5xx) ≠ signed out: surface that error so
      // callers retry instead of treating the session as ended.
      const rs = refreshErr?.response?.status;
      return Promise.reject(rs === 401 || rs === 403 ? error : refreshErr);
    }
  }
);

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default client;
